import type {
    MarkingEditorState,
    ExtendedMarkingFormData,
    TextAlignment,
    FontFamily,
} from '../../types'
import {
    DEFAULT_FONT_SIZE,
    DEFAULT_ALIGNMENT,
    DEFAULT_FONT_FAMILY,
    DEFAULT_COLORS,
} from '../../constants'
import { clampFontSize } from '../validation'
import { hasStateChanges } from '../validation'
import {
    validateAlignment,
    validateFontFamily,
} from '../validation/fieldValidation'

// Реэкспорт для удобства
export { hasStateChanges } from '../validation'

/**
 * Параметры для получения начального состояния
 */
export interface GetInitialStateParams {
    /**
     * Начальные данные из пропсов
     */
    initialData?: Partial<MarkingEditorState>
    /**
     * Сохраненные данные из БД
     */
    savedData?: Partial<ExtendedMarkingFormData>
    /**
     * Сохраненные данные из localStorage (временное решение)
     */
    localData?: Partial<MarkingEditorState>
}

/**
 * Вычисляет начальное состояние редактора маркировки
 * Объединяет данные из разных источников с учетом приоритета:
 * savedData > localData > initialData > defaults
 * 
 * @param params - параметры для получения начального состояния
 * @param params.initialData - начальные данные из пропсов компонента
 * @param params.savedData - сохраненные данные из БД
 * @param params.localData - сохраненные данные из localStorage (временное решение)
 * @returns начальное состояние редактора с валидированными значениями
 * 
 * @example
 * ```ts
 * const initialState = getInitialState({
 *   initialData: { fontSize: 14 },
 *   savedData: { mark_media_font_size: 16 },
 *   localData: { fontSize: 12 }
 * })
 * // Результат: fontSize = 16 (приоритет у savedData)
 * ```
 */
export function getInitialState(
    params: GetInitialStateParams
): MarkingEditorState {
    const { initialData, savedData, localData } = params

    return {
        markMedia: savedData?.mark_media ?? initialData?.markMedia ?? false,
        markMediaColor:
            savedData?.mark_media_color ??
            initialData?.markMediaColor ??
            DEFAULT_COLORS.dark,
        markMediaText:
            savedData?.mark_media_text ?? initialData?.markMediaText ?? null,
        fontSize:
            savedData?.mark_media_font_size !== undefined &&
            savedData?.mark_media_font_size !== null
                ? clampFontSize(Number(savedData.mark_media_font_size)) // Явно приводим к числу и применяем clamp
                : localData?.fontSize !== undefined
                  ? clampFontSize(Number(localData.fontSize)) // Явно приводим к числу и применяем clamp
                  : (initialData?.fontSize ?? DEFAULT_FONT_SIZE),
        alignment:
            validateAlignment(savedData?.mark_media_alignment) ??
            validateAlignment(localData?.alignment) ??
            validateAlignment(initialData?.alignment) ??
            DEFAULT_ALIGNMENT,
        fontFamily:
            validateFontFamily(savedData?.mark_media_font_family) ??
            validateFontFamily(localData?.fontFamily) ??
            validateFontFamily(initialData?.fontFamily) ??
            DEFAULT_FONT_FAMILY,
        hasBackground:
            initialData?.hasBackground ??
            savedData?.mark_media_has_background ??
            localData?.hasBackground ??
            false,
        backgroundColor:
            initialData?.backgroundColor ??
            savedData?.mark_media_background_color ??
            localData?.backgroundColor ??
            null,
        position:
            savedData?.mark_media_position_x !== undefined &&
            savedData?.mark_media_position_x !== null &&
            savedData?.mark_media_position_y !== undefined &&
            savedData?.mark_media_position_y !== null
                ? {
                      x: savedData.mark_media_position_x,
                      y: savedData.mark_media_position_y,
                  }
                : localData?.position !== undefined &&
                    localData?.position !== null
                  ? localData.position
                  : (initialData?.position ?? null),
    }
}

/**
 * Объединяет данные из разных источников с учетом приоритета
 * Приоритет: savedData > localData > initialData
 * Используется для частичного обновления состояния
 * 
 * @param params - параметры для объединения данных
 * @param params.initialData - начальные данные из пропсов компонента
 * @param params.savedData - сохраненные данные из БД
 * @param params.localData - сохраненные данные из localStorage
 * @returns объединенные данные состояния (частичное состояние)
 * 
 * @example
 * ```ts
 * const merged = mergeStateData({
 *   savedData: { mark_media_font_size: 16 },
 *   localData: { fontSize: 12 }
 * })
 * // Результат: { fontSize: 16 } (приоритет у savedData)
 * ```
 */
export function mergeStateData(
    params: GetInitialStateParams
): Partial<MarkingEditorState> {
    const { initialData, savedData, localData } = params

    const merged: Partial<MarkingEditorState> = {}

    // Объединяем данные с учетом приоритета
    if (savedData) {
        if (savedData.mark_media !== undefined) {
            merged.markMedia = savedData.mark_media
        }
        if (savedData.mark_media_color) {
            merged.markMediaColor = savedData.mark_media_color
        }
        if (savedData.mark_media_text !== undefined) {
            merged.markMediaText = savedData.mark_media_text
        }
        if (
            savedData.mark_media_font_size !== undefined &&
            savedData.mark_media_font_size !== null
        ) {
            merged.fontSize = clampFontSize(savedData.mark_media_font_size)
        }
        if (savedData.mark_media_alignment) {
            merged.alignment = savedData.mark_media_alignment as TextAlignment
        }
        if (savedData.mark_media_font_family) {
            merged.fontFamily = savedData.mark_media_font_family as FontFamily
        }
        if (savedData.mark_media_has_background !== undefined && savedData.mark_media_has_background !== null) {
            merged.hasBackground = savedData.mark_media_has_background
        }
        if (savedData.mark_media_background_color !== undefined) {
            merged.backgroundColor = savedData.mark_media_background_color
        }
        if (
            savedData.mark_media_position_x !== undefined &&
            savedData.mark_media_position_x !== null &&
            savedData.mark_media_position_y !== undefined &&
            savedData.mark_media_position_y !== null
        ) {
            merged.position = {
                x: savedData.mark_media_position_x,
                y: savedData.mark_media_position_y,
            }
        }
    }

    // Добавляем данные из localStorage (если не переопределены savedData)
    if (localData) {
        if (
            merged.markMedia === undefined &&
            localData.markMedia !== undefined
        ) {
            merged.markMedia = localData.markMedia
        }
        if (
            merged.markMediaColor === undefined &&
            localData.markMediaColor !== undefined
        ) {
            merged.markMediaColor = localData.markMediaColor
        }
        if (
            merged.markMediaText === undefined &&
            localData.markMediaText !== undefined
        ) {
            merged.markMediaText = localData.markMediaText
        }
        if (merged.fontSize === undefined && localData.fontSize !== undefined) {
            merged.fontSize = clampFontSize(localData.fontSize)
        }
        if (
            merged.alignment === undefined &&
            localData.alignment !== undefined
        ) {
            merged.alignment = localData.alignment
        }
        if (
            merged.fontFamily === undefined &&
            localData.fontFamily !== undefined
        ) {
            merged.fontFamily = localData.fontFamily
        }
        if (
            merged.hasBackground === undefined &&
            localData.hasBackground !== undefined
        ) {
            merged.hasBackground = localData.hasBackground
        }
        if (
            merged.backgroundColor === undefined &&
            localData.backgroundColor !== undefined
        ) {
            merged.backgroundColor = localData.backgroundColor
        }
        if (merged.position === undefined && localData.position !== undefined) {
            merged.position = localData.position
        }
    }

    // Добавляем данные из initialData (если не переопределены savedData или localData)
    if (initialData) {
        if (
            merged.markMedia === undefined &&
            initialData.markMedia !== undefined
        ) {
            merged.markMedia = initialData.markMedia
        }
        if (
            merged.markMediaColor === undefined &&
            initialData.markMediaColor !== undefined
        ) {
            merged.markMediaColor = initialData.markMediaColor
        }
        if (
            merged.markMediaText === undefined &&
            initialData.markMediaText !== undefined
        ) {
            merged.markMediaText = initialData.markMediaText
        }
        if (
            merged.fontSize === undefined &&
            initialData.fontSize !== undefined
        ) {
            merged.fontSize = clampFontSize(initialData.fontSize)
        }
        if (
            merged.alignment === undefined &&
            initialData.alignment !== undefined
        ) {
            merged.alignment = initialData.alignment
        }
        if (
            merged.fontFamily === undefined &&
            initialData.fontFamily !== undefined
        ) {
            merged.fontFamily = initialData.fontFamily
        }
        if (
            merged.hasBackground === undefined &&
            initialData.hasBackground !== undefined
        ) {
            merged.hasBackground = initialData.hasBackground
        }
        if (
            merged.backgroundColor === undefined &&
            initialData.backgroundColor !== undefined
        ) {
            merged.backgroundColor = initialData.backgroundColor
        }
        if (
            merged.position === undefined &&
            initialData.position !== undefined
        ) {
            merged.position = initialData.position
        }
    }

    return merged
}
